import React, { useState, useEffect } from 'react';
import { Percent, Save, Loader2, Pencil, Search, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import api from '../../services/api';
import { GlassButton } from '../../components/ui/GlassButton';
import { Toast } from '../../Toast';

interface UserFeeRow {
    id: string;
    full_name?: string;
    email?: string;
    display_id?: string;
    fees: {
        sale: { percentage: number; fixed_fee: number; is_override?: boolean };
        withdrawal: { percentage: number; fixed_fee: number; is_override?: boolean };
    };
}

interface GlobalFees {
    sale_percentage: number;
    sale_fixed_fee: number;
    withdrawal_percentage: number;
    withdrawal_fixed_fee: number;
}

const FALLBACK_GLOBALS: GlobalFees = {
    sale_percentage: 5,
    sale_fixed_fee: 1.20,
    withdrawal_percentage: 2,
    withdrawal_fixed_fee: 3.00,
};

const defaultForm = {
    sale_percentage: 5,
    sale_fixed_fee: 1.20,
    withdrawal_percentage: 2,
    withdrawal_fixed_fee: 3.00,
};

// ─── Seção de Taxas Globais ──────────────────────────────────────────────────

const GlobalFeesSection: React.FC = () => {
    const [fees, setFees] = useState<GlobalFees>(FALLBACK_GLOBALS);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState<GlobalFees>(FALLBACK_GLOBALS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        api.get('/banana/fees').then(res => {
            const list: { type: string; percentage: number; fixed_fee: number }[] = res.data.fees || [];
            const sale = list.find(f => f.type === 'sale');
            const wd = list.find(f => f.type === 'withdrawal');
            const loaded: GlobalFees = {
                sale_percentage: sale ? Number(sale.percentage) : FALLBACK_GLOBALS.sale_percentage,
                sale_fixed_fee: sale ? Number(sale.fixed_fee) : FALLBACK_GLOBALS.sale_fixed_fee,
                withdrawal_percentage: wd ? Number(wd.percentage) : FALLBACK_GLOBALS.withdrawal_percentage,
                withdrawal_fixed_fee: wd ? Number(wd.fixed_fee) : FALLBACK_GLOBALS.withdrawal_fixed_fee,
            };
            setFees(loaded);
            setForm(loaded);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await Promise.all([
                api.patch('/banana/fees/sale', { percentage: form.sale_percentage, fixed_fee: form.sale_fixed_fee }),
                api.patch('/banana/fees/withdrawal', { percentage: form.withdrawal_percentage, fixed_fee: form.withdrawal_fixed_fee }),
            ]);
            setFees({ ...form });
            setEditing(false);
            setToast({ msg: 'Taxas padrão atualizadas', type: 'success' });
        } catch {
            setToast({ msg: 'Erro ao salvar taxas', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <div className="mb-8">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-white font-bold text-base">Taxas Padrão Globais</h2>
                    <p className="text-gray-500 text-xs mt-0.5">Aplicadas a todos os usuários sem override individual</p>
                </div>
                {!editing && (
                    <button
                        onClick={() => { setForm({ ...fees }); setEditing(true); }}
                        className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-xs font-bold uppercase transition-colors"
                    >
                        <Pencil size={13} /> Editar
                    </button>
                )}
            </div>

            {!editing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                            <ArrowDownCircle size={20} className="text-green-400" />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Depósito / Venda</p>
                            <p className="text-white font-bold text-lg">
                                {fees.sale_percentage}% <span className="text-gray-400 text-sm font-normal">+ R$ {Number(fees.sale_fixed_fee).toFixed(2)}</span>
                            </p>
                        </div>
                    </div>
                    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                            <ArrowUpCircle size={20} className="text-orange-400" />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Saque</p>
                            <p className="text-white font-bold text-lg">
                                {fees.withdrawal_percentage}% <span className="text-gray-400 text-sm font-normal">+ R$ {Number(fees.withdrawal_fixed_fee).toFixed(2)}</span>
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Depósito */}
                        <div>
                            <p className="text-xs text-gray-500 font-bold uppercase mb-3 flex items-center gap-1.5">
                                <ArrowDownCircle size={12} className="text-green-400" /> Depósito / Venda
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] text-gray-500 block mb-1">Percentual (%)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={form.sale_percentage}
                                        onChange={e => setForm(f => ({ ...f, sale_percentage: parseFloat(e.target.value) || 0 }))}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-500 block mb-1">Fixo (R$)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={form.sale_fixed_fee}
                                        onChange={e => setForm(f => ({ ...f, sale_fixed_fee: parseFloat(e.target.value) || 0 }))}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Saque */}
                        <div>
                            <p className="text-xs text-gray-500 font-bold uppercase mb-3 flex items-center gap-1.5">
                                <ArrowUpCircle size={12} className="text-orange-400" /> Saque
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] text-gray-500 block mb-1">Percentual (%)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={form.withdrawal_percentage}
                                        onChange={e => setForm(f => ({ ...f, withdrawal_percentage: parseFloat(e.target.value) || 0 }))}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-500 block mb-1">Fixo (R$)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={form.withdrawal_fixed_fee}
                                        onChange={e => setForm(f => ({ ...f, withdrawal_fixed_fee: parseFloat(e.target.value) || 0 }))}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-5">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold transition-all disabled:opacity-50"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            Salvar Taxas
                        </button>
                        <button
                            onClick={() => setEditing(false)}
                            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Página Principal ────────────────────────────────────────────────────────

export const FeeConfig: React.FC = () => {
    const [users, setUsers] = useState<UserFeeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [editingUser, setEditingUser] = useState<UserFeeRow | null>(null);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredUsers = users.filter(u => {
        if (!searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase();
        return (u.full_name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.display_id || '').toLowerCase().includes(q);
    });

    const loadUsers = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get('/banana/users-with-fees');
            setUsers(Array.isArray(res.data?.users) ? res.data.users : []);
        } catch {
            setUsers([]);
            setLoadError('Não foi possível carregar a lista. Verifique o backend e tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadUsers(); }, []);

    const openEdit = (user: UserFeeRow) => {
        setEditingUser(user);
        setForm({
            sale_percentage: user.fees.sale.percentage,
            sale_fixed_fee: user.fees.sale.fixed_fee,
            withdrawal_percentage: user.fees.withdrawal.percentage,
            withdrawal_fixed_fee: user.fees.withdrawal.fixed_fee,
        });
    };

    const closeEdit = () => { setEditingUser(null); setForm(defaultForm); };

    const handleSave = async () => {
        if (!editingUser) return;
        setSaving(true);
        try {
            await api.patch(`/banana/users/${editingUser.id}/fees`, {
                sale_percentage: form.sale_percentage,
                sale_fixed_fee: form.sale_fixed_fee,
                withdrawal_percentage: form.withdrawal_percentage,
                withdrawal_fixed_fee: form.withdrawal_fixed_fee,
            });
            await loadUsers();
            closeEdit();
        } catch {
            alert('Erro ao salvar. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2 mb-8">
                <Percent size={28} className="text-orange-500" /> Configurar Taxas
            </h1>

            {/* Taxas Globais */}
            <GlobalFeesSection />

            {/* Divisor */}
            <div className="border-t border-white/5 mb-8" />

            {/* Override por usuário */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-white font-bold text-base">Override por Usuário</h2>
                    <p className="text-gray-500 text-xs mt-0.5">Taxas individuais que sobrescrevem o padrão global</p>
                </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-12 flex items-center justify-center gap-2 text-gray-500">
                        <Loader2 size={24} className="animate-spin" /> Carregando usuários...
                    </div>
                ) : loadError ? (
                    <div className="p-12 text-center">
                        <p className="text-red-400/90 mb-4">{loadError}</p>
                        <button onClick={loadUsers} className="text-orange-500 hover:text-orange-400 font-medium text-sm">
                            Tentar novamente
                        </button>
                    </div>
                ) : users.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">Nenhum usuário cadastrado ainda.</div>
                ) : (
                    <>
                        <div className="p-4">
                            <div className="relative w-full max-w-xs">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar usuário..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-9 pr-4 text-white text-sm focus:border-orange-500/50 outline-none"
                                />
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5">
                                    <tr>
                                        <th className="p-4 font-normal uppercase tracking-wider">Usuário</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Venda %</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Venda R$</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Saque %</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Saque R$</th>
                                        <th className="p-4 font-normal uppercase tracking-wider text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                                            <td className="p-4">
                                                <div className="flex flex-col">
                                                    <span className="text-white font-medium">{user.full_name || user.email || '—'}</span>
                                                    {user.display_id && user.display_id !== (user.email || '') && (
                                                        <span className="text-[10px] text-gray-500">{user.display_id}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <span className={user.fees.sale.is_override ? 'text-orange-400 font-semibold' : 'text-gray-400'}>
                                                    {user.fees.sale.percentage}%
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className={user.fees.sale.is_override ? 'text-orange-400 font-semibold' : 'text-gray-400'}>
                                                    R$ {Number(user.fees.sale.fixed_fee).toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className={user.fees.withdrawal.is_override ? 'text-orange-400 font-semibold' : 'text-gray-400'}>
                                                    {user.fees.withdrawal.percentage}%
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className={user.fees.withdrawal.is_override ? 'text-orange-400 font-semibold' : 'text-gray-400'}>
                                                    R$ {Number(user.fees.withdrawal.fixed_fee).toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="p-4 text-right">
                                                <button
                                                    onClick={() => openEdit(user)}
                                                    className="inline-flex items-center gap-1.5 text-orange-500 hover:text-orange-400 font-bold text-xs uppercase transition-colors"
                                                >
                                                    <Pencil size={14} /> Editar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {searchTerm.trim() && filteredUsers.length === 0 && (
                            <p className="text-center text-gray-500 text-sm py-4">Nenhum resultado para &quot;{searchTerm}&quot;</p>
                        )}
                    </>
                )}
            </div>

            {/* Modal editar override */}
            {editingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={closeEdit}>
                    <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-white mb-1">Override de taxas</h3>
                        <p className="text-sm text-gray-400 mb-6">{editingUser.full_name || editingUser.email || editingUser.id}</p>

                        <div className="space-y-5">
                            <div>
                                <p className="text-[10px] text-gray-500 font-bold uppercase mb-2 flex items-center gap-1.5">
                                    <ArrowDownCircle size={11} className="text-green-400" /> Depósito / Venda
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] text-gray-500 block mb-1">%</label>
                                        <input type="number" step="0.01" min="0" value={form.sale_percentage}
                                            onChange={e => setForm(f => ({ ...f, sale_percentage: parseFloat(e.target.value) || 0 }))}
                                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-gray-500 block mb-1">R$ fixo</label>
                                        <input type="number" step="0.01" min="0" value={form.sale_fixed_fee}
                                            onChange={e => setForm(f => ({ ...f, sale_fixed_fee: parseFloat(e.target.value) || 0 }))}
                                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] text-gray-500 font-bold uppercase mb-2 flex items-center gap-1.5">
                                    <ArrowUpCircle size={11} className="text-orange-400" /> Saque
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] text-gray-500 block mb-1">%</label>
                                        <input type="number" step="0.01" min="0" value={form.withdrawal_percentage}
                                            onChange={e => setForm(f => ({ ...f, withdrawal_percentage: parseFloat(e.target.value) || 0 }))}
                                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-gray-500 block mb-1">R$ fixo</label>
                                        <input type="number" step="0.01" min="0" value={form.withdrawal_fixed_fee}
                                            onChange={e => setForm(f => ({ ...f, withdrawal_fixed_fee: parseFloat(e.target.value) || 0 }))}
                                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <GlassButton onClick={handleSave} loading={saving} disabled={saving} className="flex-1">
                                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Salvar
                            </GlassButton>
                            <button onClick={closeEdit} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors">
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
